import { useState, useEffect, useRef } from "react";
import {
  PageShell,
  ResearchStatus,
  ResearchReport,
  ResearchForm,
  ResearchQueries,
  CurationExtraction,
  ResearchBriefings,
  QuotaNotice
} from './components';
import type { ResearchOutput, ResearchStatusType } from './types';
import { glassStyle, fadeInAnimation } from './styles';
import { API_URL, QuotaExceededError, apiFetch } from './lib/api';
import { useReportActions } from './hooks/useReportActions';
import { useResearchQuota } from './context/QuotaContext';

function App() {

  const [isResearching, setIsResearching] = useState(false);
  const [status, setStatus] = useState<ResearchStatusType | null>(null);
  const [output, setOutput] = useState<ResearchOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const [originalCompanyName, setOriginalCompanyName] = useState<string>("");
  const [, setRunId] = useState<string | null>(null);
  const [currentPhase, setCurrentPhase] = useState<'search' | 'enrichment' | 'briefing' | 'complete' | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const statusRef = useRef<HTMLDivElement>(null);
  const { quota, refresh: refreshQuota } = useResearchQuota();
  const { isGeneratingPdf, isCopied, generatePdf, copyToClipboard } = useReportActions((message) => setError(message));
  const [queries, setQueries] = useState<Array<{ text: string; number: number; category: string }>>([]);
  const [streamingQueries, setStreamingQueries] = useState<Record<string, { text: string; number: number; category: string; isComplete: boolean }>>({});
  const [isQueriesExpanded, setIsQueriesExpanded] = useState(true);
  const [enrichmentCounts, setEnrichmentCounts] = useState<{
    company: { total: number; enriched: number };
    industry: { total: number; enriched: number };
    financial: { total: number; enriched: number };
    news: { total: number; enriched: number };
  } | undefined>(undefined);
  const [briefingStatus, setBriefingStatus] = useState({
    company: false,
    industry: false,
    financial: false,
    news: false
  });
  const [isEnrichmentExpanded, setIsEnrichmentExpanded] = useState(true);
  const [isBriefingExpanded, setIsBriefingExpanded] = useState(true);
  const [hasScrolledToStatus, setHasScrolledToStatus] = useState(false);
  const [isReportStreaming, setIsReportStreaming] = useState(false);

  // Add new state for color cycling
  const [loaderColor, setLoaderColor] = useState("#468BFF");
  
  // Scroll helper function
  const scrollToStatus = () => {
    if (!hasScrolledToStatus && statusRef.current) {
      const yOffset = -20;
      const y = statusRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setHasScrolledToStatus(true);
    }
  };

  // Add useEffect for color cycling
  useEffect(() => {
    if (!isResearching) return;
    
    const colors = [
      "#468BFF", // Blue
      "#8FBCFA", // Light Blue
      "#FE363B", // Red
      "#FF9A9D", // Light Red
      "#FDBB11", // Yellow
      "#F6D785", // Light Yellow
    ];
    
    let currentIndex = 0;
    
    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % colors.length;
      setLoaderColor(colors[currentIndex]);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isResearching]);

  const resetResearch = () => {
    setIsResetting(true);
    
    // Use setTimeout to create a smooth transition
    setTimeout(() => {
      setStatus(null);
      setOutput(null);
      setError(null);
      setIsComplete(false);
      setRunId(null);
      setCurrentPhase(null);
      setQueries([]);
      setStreamingQueries({});
      setEnrichmentCounts(undefined);
      setBriefingStatus({
        company: false,
        industry: false,
        financial: false,
        news: false
      });
      setIsQueriesExpanded(true);
      setIsEnrichmentExpanded(true);
      setIsBriefingExpanded(true);
      setHasScrolledToStatus(false);
      setIsReportStreaming(false);
      setIsResetting(false);
    }, 300);
  };

  // Stream research results via SSE
  const streamResults = (jobId: string) => {
    const eventSource = new EventSource(`${API_URL}/research/${jobId}/stream`, { withCredentials: true });
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Helper function to map node names to user-friendly step names
        const getStepName = (nodeName: string): string => {
          const stepMap: Record<string, string> = {
            'grounding': 'Busca',
            'financial_analyst': 'Busca',
            'news_scanner': 'Busca',
            'industry_analyst': 'Busca',
            'company_analyst': 'Busca',
            'collector': 'Busca',
            'curator': 'Enriquecendo',
            'enricher': 'Enriquecendo',
            'briefing': 'Resumo',
            'editor': 'Finalizando'
          };
          return stepMap[nodeName] || nodeName;
        };

        // Handle progress events from backend (node transitions)
        if (data.type === 'progress' && data.step) {
          const stepName = getStepName(data.step);
          setStatus({
            step: stepName,
            message: `Processando ${data.step}...`
          });
          
          // Update phase based on step
          if (['grounding', 'financial_analyst', 'news_scanner', 'industry_analyst', 'company_analyst', 'collector'].includes(data.step)) {
            setCurrentPhase('search');
          } else if (['curator', 'enricher'].includes(data.step)) {
            setCurrentPhase('enrichment');
          } else if (data.step === 'briefing') {
            setCurrentPhase('briefing');
          }
          
          scrollToStatus();
        }
        
        // Direct event-to-phase mapping
        if (data.type === 'query_generating') {
          // Show query being generated and update streaming queries
          setCurrentPhase('search');
          setStatus({
            step: 'Busca',
            message: `Consulta ${data.query_number}: ${data.query}`
          });
          // Update streaming queries with current partial query
          const key = `${data.category}_${data.query_number}`;
          setStreamingQueries(prev => ({
            ...prev,
            [key]: {
              text: data.query,
              number: data.query_number,
              category: data.category,
              isComplete: false
            }
          }));
        } else if (data.type === 'query_generated') {
          // Show completed query and move to queries list
          setCurrentPhase('search');
          setStatus({
            step: 'Busca',
            message: `Gerada: ${data.query}`
          });
          // Add to completed queries
          setQueries(prev => [...prev, {
            text: data.query,
            number: data.query_number,
            category: data.category
          }]);
          // Remove from streaming queries
          const key = `${data.category}_${data.query_number}`;
          setStreamingQueries(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
          });
          scrollToStatus();
        } else if (data.type === 'research_init') {
          // Show research initialization
          setCurrentPhase('search');
          setStatus({
            step: 'Inicializando',
            message: data.message || `Iniciando pesquisa para ${data.company}`
          });
        } else if (data.type === 'crawl_start') {
          // Show website crawl starting
          setCurrentPhase('search');
          setStatus({
            step: 'Rastreamento do site',
            message: data.message || 'Rastreando o site da empresa'
          });
        } else if (data.type === 'curation') {
          // Show curation progress - transition to enrichment phase
          setCurrentPhase('enrichment');
          setStatus({
            step: 'Organizando dados',
            message: data.message || `Organizando documentos de ${data.category}`
          });
          // Initialize enrichment counts when curation starts for a category
          if (data.category) {
            setEnrichmentCounts(prev => ({
              ...prev,
              [data.category]: {
                total: data.total || 0,
                enriched: 0
              }
            } as typeof enrichmentCounts));
          }
          // Collapse queries section when moving to enrichment
          setTimeout(() => {
            setIsQueriesExpanded(false);
          }, 1000);
          scrollToStatus();
        } else if (data.type === 'enrichment') {
          // Show enrichment progress
          setCurrentPhase('enrichment');
          setStatus({
            step: 'Enriquecendo',
            message: data.message || 'Enriquecendo documentos com conteúdo adicional'
          });
          // Update enriched count if provided
          if (data.category && data.enriched !== undefined) {
            const category = data.category as 'company' | 'industry' | 'financial' | 'news';
            setEnrichmentCounts(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                [category]: {
                  total: prev[category]?.total || data.total || 0,
                  enriched: data.enriched
                }
              } as typeof enrichmentCounts;
            });
          }
        } else if (data.type === 'briefing_start') {
          // Show briefing generation starting
          setCurrentPhase('briefing');
          setStatus({
            step: 'Gerando resumos',
            message: `Criando resumo de ${data.category} a partir de ${data.total_docs} documentos`
          });
          // Collapse enrichment section when moving to briefing
          setTimeout(() => {
            setIsEnrichmentExpanded(false);
          }, 1000);
          scrollToStatus();
        } else if (data.type === 'briefing_complete') {
          // Show briefing completion and mark category as complete
          setCurrentPhase('briefing');
          setStatus({
            step: 'Resumo concluído',
            message: `Resumo de ${data.category} gerado (${data.content_length} caracteres)`
          });
          // Mark briefing as complete for this category
          if (data.category) {
            setBriefingStatus(prev => {
              const newBriefingStatus = {
                ...prev,
                [data.category]: true
              };
              
              // Check if all briefings are complete
              const allBriefingsComplete = Object.values(newBriefingStatus).every(status => status);
              
              // Collapse briefing section when all briefings are complete
              if (allBriefingsComplete) {
                setTimeout(() => {
                  setIsBriefingExpanded(false);
                }, 2000);
              }
              
              return newBriefingStatus;
            });
          }
        } else if (data.type === 'report_compilation') {
          // Show report compilation
          setCurrentPhase('briefing');
          setStatus({
            step: 'Finalizando relatório',
            message: data.message || 'Compilando relatório final'
          });
        } else if (data.type === 'report_chunk' && data.chunk) {
          // Stream report chunks as they arrive
          setIsReportStreaming(true);
          setOutput((prev) => {
            const currentReport = prev?.details?.report || '';
            return {
              summary: "",
              details: { report: currentReport + data.chunk },
            };
          });
          setStatus({
            step: 'Finalizando relatório',
            message: 'Gerando relatório final...'
          });
        } else if (data.type === 'complete' && data.report) {
          setIsReportStreaming(false);
          setOutput({
            summary: "",
            details: { report: data.report },
          });
          setStatus({ step: "Concluído", message: "Pesquisa concluída com sucesso" });
          setIsComplete(true);
          setIsResearching(false);
          eventSource.close();
          refreshQuota();
        } else if (data.type === 'error') {
          setError(data.error);
          setIsResearching(false);
          eventSource.close();
          refreshQuota();
        }
      } catch (err) {
        console.error('Error parsing SSE data:', err);
      }
    };

    eventSource.onerror = () => {
      setError('Conexão perdida ou erro no servidor');
      setIsResearching(false);
      eventSource.close();
      refreshQuota();
    };
  };

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  // Create a custom handler for the form that receives form data
  const handleFormSubmit = async (formData: {
    companyName: string;
    companyUrl: string;
    companyHq: string;
    companyIndustry: string;
  }) => {

    // Clear any existing errors first
    setError(null);

    // If research is complete, reset the UI first
    if (isComplete) {
      resetResearch();
      await new Promise(resolve => setTimeout(resolve, 300)); // Wait for reset animation
    }

    // Clear any existing SSE connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setIsResearching(true);
    setOriginalCompanyName(formData.companyName);
    setStatus({
      step: "Processando",
      message: "Iniciando pesquisa..."
    });

    try {
      // Format the company URL if provided
      const formattedCompanyUrl = formData.companyUrl
        ? formData.companyUrl.startsWith('http://') || formData.companyUrl.startsWith('https://')
          ? formData.companyUrl
          : `https://${formData.companyUrl}`
        : undefined;

      const requestData = {
        company: formData.companyName,
        company_url: formattedCompanyUrl,
        industry: formData.companyIndustry || undefined,
        hq_location: formData.companyHq || undefined,
      };

      const response = await apiFetch("/research", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.job_id) {
        setRunId(data.run_id ?? null);
        streamResults(data.job_id);
      } else {
        throw new Error("Nenhum ID de pesquisa recebido");
      }
    } catch (err) {
      if (err instanceof QuotaExceededError) {
        const window = err.quota[err.quota.blocked_by || "daily"];
        const resetLabel = new Intl.DateTimeFormat("pt-BR", {
          dateStyle: "short",
          timeStyle: "short",
        }).format(new Date(window.resets_at));
        setError(`Cota de pesquisas atingida. Libera em ${resetLabel} (${err.quota.time_zone}).`);
        refreshQuota();
      } else {
        setError(err instanceof Error ? err.message : "Falha ao iniciar a pesquisa");
      }
      setIsResearching(false);
    }
  };

  // Add new function to handle PDF generation
  const handleGeneratePdf = () => generatePdf(output?.details?.report || "", originalCompanyName);
  const handleCopyToClipboard = () => copyToClipboard(output?.details?.report || "");

  const isBlocked = Boolean(quota?.blocked_by) && !quota?.exempt;

  return (
    <PageShell showHero>
        {/* Quota Notice */}
        <QuotaNotice />

        {/* Form Section */}
        <ResearchForm
          onSubmit={handleFormSubmit}
          isResearching={isResearching}
          isBlocked={isBlocked}
          glassStyle={glassStyle}
          loaderColor={loaderColor}
        />

        {/* Error Message */}
        {error && (
          <div
            className={`${glassStyle.card} border-[#FE363B]/30 bg-[#FE363B]/10 ${fadeInAnimation.fadeIn} ${isResetting ? 'opacity-0 transform -translate-y-4' : 'opacity-100 transform translate-y-0'} font-['DM_Sans']`}
          >
            <p className="text-[#FE363B]">{error}</p>
          </div>
        )}

        {/* Status Box */}
        <ResearchStatus
          status={status}
          error={error}
          isComplete={isComplete}
          currentPhase={currentPhase}
          isResetting={isResetting}
          glassStyle={glassStyle}
          loaderColor={loaderColor}
          statusRef={statusRef}
        />

        {/* Research Report - always at the top when available */}
        {output && output.details && (
          <ResearchReport
            output={{
              summary: output.summary,
              details: {
                report: output.details.report || ''
              }
            }}
            isResetting={isResetting}
            isStreaming={isReportStreaming}
            glassStyle={glassStyle}
            fadeInAnimation={fadeInAnimation}
            loaderColor={loaderColor}
            isGeneratingPdf={isGeneratingPdf}
            isCopied={isCopied}
            onCopyToClipboard={handleCopyToClipboard}
            onGeneratePdf={handleGeneratePdf}
          />
        )}

        {/* Research Briefings - show once briefing starts and keep visible */}
        {(currentPhase === 'briefing' || currentPhase === 'complete') && (
          <ResearchBriefings
            briefingStatus={briefingStatus}
            isExpanded={isBriefingExpanded}
            onToggleExpand={() => setIsBriefingExpanded(!isBriefingExpanded)}
            isResetting={isResetting}
          />
        )}

        {/* Curation and Extraction - show once enrichment starts and keep visible */}
        {(currentPhase === 'enrichment' || currentPhase === 'briefing' || currentPhase === 'complete') && enrichmentCounts && (
          <CurationExtraction
            enrichmentCounts={enrichmentCounts}
            isExpanded={isEnrichmentExpanded}
            onToggleExpand={() => setIsEnrichmentExpanded(!isEnrichmentExpanded)}
            isResetting={isResetting}
            loaderColor={loaderColor}
          />
        )}

        {/* Research Queries - always at the bottom when visible */}
        {(queries.length > 0 || Object.keys(streamingQueries).length > 0) && (
          <ResearchQueries
            queries={queries}
            streamingQueries={streamingQueries}
            isExpanded={isQueriesExpanded}
            onToggleExpand={() => setIsQueriesExpanded(!isQueriesExpanded)}
            isResetting={isResetting}
            glassStyle={glassStyle.card}
          />
        )}
    </PageShell>
  );
}

export default App;